import React, { useState, useEffect } from 'react';
import { Input, Button, Card, Typography, Alert, Flex, Space } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/TranslationContext';
import logo from '../../assets/logo.png';
import VerificationModal from '../../components/Auth/VerificationModal';

const { Text } = Typography;

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoVisible, setLogoVisible] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [userId, setUserId] = useState(null);
  const { login, completeLogin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    setTimeout(() => setLogoVisible(true), 100);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);

      if (result.success) {
        if (result.requiresVerification) {
          setUserId(result.userId);
          setShowVerification(true);
        } else {
          const lang = localStorage.getItem('preferredLanguage') || 'en';
          navigate(`/${lang}/dashboard`);
        }
      } else {
        setError(result.error || t('auth.loginFailed'));
      }
    } catch (err) {
      setError(t('auth.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSuccess = (authData) => {
    completeLogin(authData);
    setShowVerification(false);
    const lang = localStorage.getItem('preferredLanguage') || 'en';
    navigate(`/${lang}/dashboard`);
  };

  const handleCloseVerification = () => {
    setShowVerification(false);
    setUserId(null);
    setPassword('');
  };

  return (
    <Flex
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #172d4a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorations */}
      <div
        style={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '40%',
          height: '40%',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(100px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-15%',
          left: '-10%',
          width: '50%',
          height: '50%',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
          filter: 'blur(100px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '30%',
          height: '30%',
          borderRadius: '50%',
          background: 'rgba(30, 58, 95, 0.3)',
          filter: 'blur(120px)',
        }}
      />

      <Flex
        style={{ position: 'relative', zIndex: 1, width: '100%', padding: 24 }}
        justify="center"
        align="center"
      >
        <Card
          style={{
            maxWidth: 480,
            width: '100%',
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            borderRadius: 12,
          }}
          styles={{ body: { padding: 40 } }}
        >
          <Flex vertical align="center" gap={24} style={{ marginBottom: 24 }}>
            <div
              style={{
                padding: 24,
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #172d4a 100%)',
                borderRadius: 12,
                boxShadow: '0 8px 32px rgba(30, 58, 95, 0.3)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <img src={logo} alt="Bidmanager" style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }} />
            </div>
            <Text
              strong
              style={{
                fontSize: 16,
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #172d4a 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t('auth.loginSubtitle')}
            </Text>
          </Flex>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <form onSubmit={handleSubmit}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Input
                size="large"
                placeholder={t('auth.usernamePlaceholder')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                autoComplete="username"
                style={{ backgroundColor: '#f8f9fa' }}
              />
              <Input.Password
                size="large"
                placeholder={t('auth.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
                style={{ backgroundColor: '#f8f9fa' }}
              />
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                block
                style={{
                  height: 48,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
                }}
              >
                {loading ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
              <Flex justify="center">
                <Button
                  type="link"
                  onClick={() => navigate('/forgot-password')}
                  style={{ color: '#1e3a5f', fontWeight: 500 }}
                >
                  {t('auth.forgotPasswordQuestion')}
                </Button>
              </Flex>
            </Space>
          </form>
        </Card>
      </Flex>

      <VerificationModal
        open={showVerification}
        userId={userId}
        onSuccess={handleVerificationSuccess}
        onClose={handleCloseVerification}
      />
    </Flex>
  );
};

export default Login;
